import { FormProvider } from "react-hook-form";
import { useHistory, useLocation } from "react-router-dom";

import { useAuthContext } from "@/components/AuthProvider";
import Box from "@/components/Box";
import CustomerPage from "@/components/CustomerPage";
import ResendVerificationEmailButton from "@/components/ResendVerificationEmailButton";
import { theme } from "@/core/theme";
import { HOME_URL } from "@/core/urls";
import { LOG_IN_MUTATION } from "@/graphql/accounts";
import useDjangoGraphqlForm from "@/hooks/form/useDjangoGraphqlForm";
import { needsOtpAction } from "@/hooks/useOtpApi";
import useDocTitle from "@/hooks/use-doc-title";

import { PanelBoxV2 } from "./Boxes";
import Center from "./Center";
import ControlledFormField from "./ControlledFormField";
import FloatingLabelInput from "./FloatingLabelInput";
import FloatingLabelPasswordInput from "./PasswordInput";
import Stack from "./Stack";
import B2CHeader from "./header/B2CHeader";

function NonFieldError({ error }) {
  if (!error) return null;

  return (
    <Box color="red" py={20}>
      {error}
    </Box>
  );
}

export function LoginForm({ bottomLinks, generateHubspotToken, ...props }) {
  const history = useHistory();
  const location = useLocation();
  const { setCsrfToken, refreshAuth } = useAuthContext();

  const { methods, onSubmit, nonFieldError } = useDjangoGraphqlForm({
    mutation: LOG_IN_MUTATION,
    mutationName: "logInMutation",
    defaultValues: {
      email: "",
      password: "",
      generateHubspotToken
    },
    handleSuccess: async ({ data }) => {
      setCsrfToken(data.logInMutation.csrfToken);
      if (window.HubSpotConversations?.widget) {
        window.HubSpotConversations.widget.remove();
      }

      const userData = await refreshAuth();

      if (!needsOtpAction(userData.otp)) {
        history.push(new URLSearchParams(location.search).get("next") || HOME_URL);
      }
    }
  });

  return (
    <FormProvider {...methods}>
      <PanelBoxV2
        maxWidth={600}
        stacked
        gap={40}
        outer={{
          as: "form",
          onSubmit: methods.handleSubmit(onSubmit),
          ...props
        }}
      >
        <Stack gap={20}>
          <NonFieldError error={nonFieldError} />
          <ControlledFormField name="email" label="Email address" Component={FloatingLabelInput} />
          <ControlledFormField
            name="password"
            label="Password"
            Component={FloatingLabelPasswordInput}
          />
        </Stack>
        <Center gap={20} stacked>
          <ResendVerificationEmailButton errorField="email" />
        </Center>
        <Center gap={20} stacked>
          {bottomLinks}
        </Center>
      </PanelBoxV2>
    </FormProvider>
  );
}

export default function Login({ bg, color, title, bottomLinks }) {
  useDocTitle("Sign in");
  return (
    <>
      <B2CHeader />
      <CustomerPage
        jumbotronProps={{
          title,
          bg,
          color
        }}
      >
        <LoginForm
          generateHubspotToken={false}
          bottomLinks={bottomLinks}
          pt={theme.spacing.section.pt}
          pb={theme.spacing.section.pb}
          px={20}
        />
      </CustomerPage>
    </>
  );
}

Login.defaultProps = {
  title: "Sign In"
};
